import { useRef, useCallback } from 'react'
import { collection, addDoc, doc, setDoc, increment, serverTimestamp } from 'firebase/firestore'
import { db } from '../config/firebase'
import { useAuth } from '../context/AuthContext'
import { getAppLanguage } from '../i18n/translations'

const MIN_PLAY_SECONDS = 3 // ignore very short plays (accidental taps)

/**
 * Track lesson usage to Firestore for signed-in users.
 *
 * Writes to two collections:
 * - `activity`: individual play events (lesson, duration, completion %)
 * - `usageStats`: per-user aggregated counters (total plays, lessons touched, etc.)
 */
export function useUsageTracking() {
  const { user } = useAuth()
  const sessionsRef = useRef({}) // { [sectionId]: { startTime, lastTime, maxPercent } }

  // Call when a lesson starts playing
  const trackPlay = useCallback((sectionId, categoryId) => {
    if (!user || !sectionId) return
    sessionsRef.current[sectionId] = {
      startTime: Date.now(),
      lastTime: Date.now(),
      categoryId,
      maxPercent: 0
    }
  }, [user])

  // Call during playback to update progress (throttled by useProgress already)
  const trackProgress = useCallback((sectionId, currentTime, duration) => {
    if (!user || !sectionId || !duration) return
    const session = sessionsRef.current[sectionId]
    if (!session) return
    session.lastTime = Date.now()
    session.maxPercent = Math.max(session.maxPercent, Math.min(currentTime / duration, 1))
  }, [user])

  // Call when playback stops (pause, end, or navigate away)
  const trackEnd = useCallback(async (sectionId) => {
    if (!user || !sectionId) return
    const session = sessionsRef.current[sectionId]
    if (!session) return
    delete sessionsRef.current[sectionId]

    const playSeconds = Math.round((session.lastTime - session.startTime) / 1000)
    if (playSeconds < MIN_PLAY_SECONDS) return // skip accidental plays

    const language = getAppLanguage()
    const completed = session.maxPercent >= 0.8

    try {
      // Log individual play event
      await addDoc(collection(db, 'activity'), {
        uid: user.uid,
        sectionId,
        categoryId: session.categoryId || null,
        language,
        playSeconds,
        maxPercent: Math.round(session.maxPercent * 100),
        completed,
        createdAt: serverTimestamp()
      })

      // Update per-user aggregate stats
      const statsRef = doc(db, 'usageStats', user.uid)
      await setDoc(statsRef, {
        totalPlays: increment(1),
        totalSeconds: increment(playSeconds),
        completions: increment(completed ? 1 : 0),
        lastActiveAt: serverTimestamp(),
        [`lang_${language}`]: increment(1)
      }, { merge: true })
    } catch (err) {
      // Non-critical — don't disrupt the user experience
      console.error('Usage tracking error:', err)
    }
  }, [user])

  // Flush all active sessions (call on unmount or navigation)
  const flushAll = useCallback(() => {
    const ids = Object.keys(sessionsRef.current)
    ids.forEach(id => trackEnd(id))
  }, [trackEnd])

  return { trackPlay, trackProgress, trackEnd, flushAll }
}
